import { Box, Container, Grid2 } from "@mui/material";
import { useRef } from "react";
import { Link } from "react-router-dom";

export const ReserveAdequacyMonitoringDetail = () => {
  const productSectionRef = useRef(null);
  const valueSectionRef = useRef(null);
  const financialSectionRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    if (!sectionRef?.current) return;
    const sectionTop =
      sectionRef.current.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: sectionTop - 110, behavior: "smooth" });
  };

  return (
  <Container maxWidth="lg" className="pt-6">
      <Box className="mb-8 md:mb-10 rounded-xl border border-[#021744] bg-[#021744]">
        <Box className="bg-[#021744] text-white rounded-xl p-6 md:p-10">
          <Box className="w-full flex flex-wrap justify-between items-start gap-4 mb-6">
            <span className="inline-flex text-xs px-3 py-1 rounded-full bg-[#42DDD1] border border-[#42DDD1] text-[#021744]">
              Use Case Spotlight
            </span>
            <Box className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => scrollToSection(valueSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Value
              </button>
              <button
                type="button"
                onClick={() => scrollToSection(financialSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Financial Impact
              </button>
              <button
                type="button"
                onClick={() => scrollToSection(productSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Product
              </button>
            </Box>
          </Box>
          <h1 className="text-left text-[26px] md:text-[34px] tracking-normal leading-10 font-medium mb-4">
            Stay in Reserve Range — Detect Mismatches Before They Become Leakage
          </h1>
          <p className="text-[#d6e6ff] leading-7 mb-4 text-left">
            For claims executives, managers, supervisors, and actuaries, reserve
            adequacy isn't just a financial metric—it’s the backbone of
            regulatory compliance, efficient claims management, and accurate
            actuarial analysis. But rising claim severity, unpredictable
            litigation, and the loss of tribal knowledge from a retiring
            workforce are creating serious gaps in reserving accuracy.
            Traditional spreadsheet-driven audits can’t spot these gaps in time.
          </p>
          <p className="text-[#d6e6ff] leading-7 text-left">
            That’s where 4SeeCharlee steps in. By applying pre-trained reserving
            patterns learned from over 55 million claims—and continuously
            refined across carriers—Charlee’s NLP-powered AI flags reserve
            mismatches in real time. It highlights under- and over-reserved open
            claims by identifying patterns learned across carriers, empowering
            teams to take proactive action on these inconsistencies before small
            gaps become financial blind spots.
          </p>
        </Box>
      </Box>
      <Grid2 container spacing={2} className="mb-8 items-stretch">
        <Grid2 size={{ xs: 12, md: 3 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">Role</p>
            <p className="text-sm text-[#1a2f55]">
              Claims Executives, Claims Managers, Claims Supervisors, Actuaries
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 3 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">
              Job at Hand
            </p>
            <p className="text-sm text-[#1a2f55]">
              Maintain accurate reserves and financial reporting
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 3 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">Trigger</p>
            <p className="text-sm text-[#1a2f55]">
              Monthly reserve adequacy audit flagged inconsistencies
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 3 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">
              Pain Point
            </p>
            <p className="text-sm text-[#1a2f55]">
              Claims incurring higher than expected, with potential reserve
              deficiencies
            </p>
          </Box>
        </Grid2>
      </Grid2>
      <Grid2
        ref={productSectionRef}
        container
        spacing={2}
        className="mb-4 items-stretch"
      >
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 h-full">
            <h4 className="text-[#021744] font-medium mb-2">Before Charlee</h4>
            <p className="text-[#42506a]">
              Spreadsheet-driven manual review of open claims for reserve
              adjustments, IBNR irregularities, and reserving/profitability
              issues.
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 h-full">
            <h4 className="text-[#021744] font-medium mb-2">With Charlee</h4>
            <p className="text-[#42506a]">
              AI continuously monitors reserves vs. predicted incurred, flags
              under- and over-reserved risks, and surfaces high-impact claims
              for review.
            </p>
          </Box>
        </Grid2>
      </Grid2>
      <Grid2 container spacing={2} className="mb-4 items-stretch">
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">Product Used</p>
            <p>🔎 4SeeCharlee</p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">LOBs</p>
            <p>All</p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">Area</p>
            <Box className="flex gap-2">
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Claims
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Reserves
              </span>
            </Box>
          </Box>
        </Grid2>
      </Grid2>
      <Box
        ref={valueSectionRef}
        className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4"
      >
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Value Proposition
        </h3>
        <ul className="text-[#42506a] space-y-2 text-left">
          <li>
            • Early reserve adequacy assessment benefits both present and future
            processes.
          </li>
          <li>
            • Claims can be reviewed sooner based on claim age, potentially
            accelerating reserve decisions.
          </li>
          <li>
            • Early reviews ensure timely data collection, improving outcomes
            and reducing litigation.
          </li>
          <li>
            • Increased productivity and precision in financial reporting and
            reserve setting.
          </li>
          <li>
            • Insights can be used to train staff and improve long-term reserve
            practices.
          </li>
        </ul>
      </Box>
      <Box
        ref={financialSectionRef}
        className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4"
      >
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Financial Impact
        </h3>
        <p className="text-[#42506a] mb-2 text-left">
          Inaccurate reserves can lead to:
        </p>
        <ul className="text-[#42506a] space-y-2 mb-3 text-left">
          <li>
            • Regulatory scrutiny, particularly when under-reserving forces late
            reserve strengthening
          </li>
          <li>
            • Over-reserving, tying up capital and inflating liability estimates
          </li>
          <li>
            • Late adjustments, distorting financial reports and complicating
            reinsurance/actuarial operations
          </li>
        </ul>
        <Box className="rounded-lg border border-[#E6EAF2] bg-[#EDF3F8] p-4 text-left">
          <p className="text-[#021744] font-bold mb-2">Charlee delivers:</p>
          <ul className="text-[#42506a] space-y-2 text-left">
            <li>
              • $5K–$15K estimated savings per under-reserved claim due to early
              corrections
            </li>
            <li>• Reduced capital drag by optimizing over-reserved claims</li>
            <li>• voidance of reserve leakage and surprise adjustments</li>
            <li>• Improved quarter/year-end closing accuracy and stability</li>
            <li>
              • Millions in reported reserve optimization gains from real-world
              deployments
            </li>
          </ul>
        </Box>
      </Box>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <h3 className="text-[28px] md:text-[32px] text-[#021744] font-bold mb-4">
          Results
        </h3>
        <Grid2 container spacing={2} className="items-stretch">
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                📈 18%
              </p>
              <p className="text-[#42506a]">improvement in reserve accuracy</p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                ✅ 30%
              </p>
              <p className="text-[#42506a]">
                reduction in late reserve adjustments
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p>💬</p>
              <p className="text-[#42506a]">
                “Now our actuaries, supervisors, and adjusters are finally
                aligned.”
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                ⏱️ 2 weeks
              </p>
              <p className="text-[#42506a]">
                Dashboards ready; rapid time-to-value
              </p>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pt-8 text-center">
        <Link
          to="/blogs/usecases"
          className="inline-flex items-center gap-2 text-[#021744] border border-[#021744] bg-white rounded-full px-6 py-3 hover:bg-[#021744] hover:text-white"
        >
          Back to Use Cases
        </Link>
      </Box>
    </Container>
  );
};
